// /modifiers — Item Modifier Management (PAGES.md §3.103)
//
// Manage modifier groups for restaurant orders: spice level, add-ons,
// cooking preferences. Each group has options with price adjustments,
// required/optional flag, max selections, and linked products.
// RESTAURANT vertical only — MANAGER+ (level 30).
// Traceability: FR-ORD-035

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  PageContainer,
  Button,
  Badge,
  Input,
  Select,
  Toggle,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Modal,
  ConfirmDialog,
  FormField,
  DataTable,
  Pagination,
  Tooltip,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, SelectOption } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { modifierService } from '@/services/modifier.service';
import type { ModifierGroup, ModifierStats } from '@/services/modifier.service';
import { deriveModifierKpis, priceMinorToDisplay, priceDisplayToMinor } from './modifier-utils';
import { productService } from '@/services/product.service';
import { locationService } from '@/services/location.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { GLASS, GLASS_FILTER, cn, toneClasses } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';

// ─── Role levels ────────────────────────────────────────────────────────────

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

// ─── Icons ──────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function SlidersIcon({ className = 'h-14 w-14' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}

// ─── KPI Card (Glass Depth + tone gradient) ─────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'danger' | 'violet' | 'teal' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4 animate-slide-up')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono mt-1">{value}</p>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
let optionKeyCounter = 0;

function paiseToPriceRange(options: { price_adjustment: number }[], currency: CurrencyCode): string {
  if (options.length === 0) return '—';
  const prices = options.map((o) => o.price_adjustment);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return formatPaise(min, currency);
  return `${formatPaise(min, currency)} – ${formatPaise(max, currency)}`;
}

// ─── Form types ─────────────────────────────────────────────────────────────

interface OptionFormRow {
  key: string;
  name: string;
  price_display: string;
}

interface FormState {
  name: string;
  location_id: string;
  required: boolean;
  max_selections: string;
  options: OptionFormRow[];
  product_ids: string[];
}

const EMPTY_OPTION = (): OptionFormRow => ({
  key: `opt-${Date.now()}-${++optionKeyCounter}`,
  name: '',
  price_display: '0',
});

interface FormErrors {
  name?: string;
  location_id?: string;
  options?: string;
  option_names?: Record<number, string>;
}

// ─── Product type (minimal) ─────────────────────────────────────────────────

interface ProductMinimal {
  id: string;
  name: string;
}

interface LocationMinimal {
  id: string;
  name: string;
}

// ─── Mobile Card (xs–sm breakpoints) ────────────────────────────────────────

function ModifierMobileCard({
  group,
  onEdit,
  onDelete,
}: {
  group: ModifierGroup;
  onEdit: (g: ModifierGroup) => void;
  onDelete: (g: ModifierGroup) => void;
}) {
  const currency = useTenantCurrency();
  return (
    <div className={cn(GLASS, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onEdit(group)}
            className="font-bold text-neutral-900 dark:text-neutral-100 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-left text-sm truncate block"
          >
            {group.name}
          </button>
          <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
            <span className="font-mono tabular-nums">{group.options.length} option{group.options.length !== 1 ? 's' : ''}</span>
            <span className="font-mono tabular-nums">{paiseToPriceRange(group.options, currency)}</span>
            <span className="font-mono tabular-nums">{group.product_ids.length} linked</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge variant={group.required ? 'warning' : 'draft'} size="sm">
            {group.required ? 'Required' : 'Optional'}
          </Badge>
        </div>
      </div>
      <div className="flex items-center justify-end gap-1 mt-3 border-t border-neutral-100 dark:border-neutral-800 pt-3">
        <Button variant="ghost" size="sm" onClick={() => onEdit(group)} aria-label={`Edit ${group.name}`}>
          <PencilIcon />
          <span className="ml-1 text-xs">Edit</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(group)}
          className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
          aria-label={`Delete ${group.name}`}
        >
          <TrashIcon />
        </Button>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function ModifierManagementContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isOwnerPlus = roleLevel >= ROLE_LEVELS.OWNER;

  // ── Data ────────────────────────────────────────────────────────────────
  const [groups, setGroups] = useState<ModifierGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ModifierStats | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // ── Modal ───────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ModifierGroup | null>(null);
  const [form, setForm] = useState<FormState>({
    name: '',
    location_id: selectedLocationId ?? '',
    required: false,
    max_selections: '',
    options: [EMPTY_OPTION()],
    product_ids: [],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [products, setProducts] = useState<ProductMinimal[]>([]);
  const [locations, setLocations] = useState<LocationMinimal[]>([]);

  // ── Delete ──────────────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<ModifierGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch groups (server-side search) ──────────────────────────────────
  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    try {
      const [result, statsResult] = await Promise.all([
        modifierService.list({
          location_id: selectedLocationId ?? undefined,
          search: search || undefined,
          page,
          limit: PAGE_SIZE,
        }),
        modifierService.getStats({
          location_id: selectedLocationId ?? undefined,
        }),
      ]);
      setGroups(result.data);
      setTotal(result.meta?.total ?? result.data.length);
      setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load modifiers.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, page, search, toast]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // ── Fetch products for linker ───────────────────────────────────────────
  const fetchProducts = useCallback(async (locationId?: string) => {
    try {
      const result = await productService.list({
        location_id: locationId ?? selectedLocationId ?? undefined,
        limit: 200,
      });
      setProducts((result.data as unknown as ProductMinimal[]).map((p) => ({ id: p.id, name: p.name })));
    } catch {
      // silent
    }
  }, [selectedLocationId]);

  // ── Fetch locations for OWNER+ ──────────────────────────────────────────
  const fetchLocations = useCallback(async () => {
    if (!isOwnerPlus) return;
    try {
      const result = await locationService.list({ limit: 100, is_active: 'true' });
      setLocations((result.data as unknown as LocationMinimal[]).map((l) => ({ id: l.id, name: l.name })));
    } catch {
      // silent
    }
  }, [isOwnerPlus]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen) { e.preventDefault(); openCreateModal(); }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen]);

  // ── KPIs ────────────────────────────────────────────────────────────────
  // Derived from the tenant-/location-wide `/modifiers/stats` aggregate (not the
  // current page slice) so the cards reflect every group and stay constant as
  // you paginate (GitHub issue #285).
  const { requiredGroups, avgOptions } = deriveModifierKpis(stats);

  // ── Location options for Select ─────────────────────────────────────────
  // OWNER+ picks from every location; a MANAGER from the locations assigned to them.
  const locationOptions: SelectOption[] = useMemo(() =>
    (isOwnerPlus ? locations : (profile?.locations ?? []).filter((l) => l.is_active))
      .map((l) => ({ value: l.id, label: l.name })),
  [isOwnerPlus, locations, profile?.locations]);
  // The header's location, else the user's only location — a single-location MANAGER
  // has no header switcher, and the form would otherwise have no location to file under.
  const defaultLocationId =
    selectedLocationId ?? (locationOptions.length === 1 ? locationOptions[0].value : '');

  // ── Product options for multi-select ────────────────────────────────────
  const productOptions: SelectOption[] = useMemo(() =>
    products.map((p) => ({ value: p.id, label: p.name })),
  [products]);

  // ── Modal helpers ───────────────────────────────────────────────────────
  const openCreateModal = useCallback(() => {
    setEditingGroup(null);
    setForm({
      name: '',
      location_id: defaultLocationId,
      required: false,
      max_selections: '',
      options: [EMPTY_OPTION()],
      product_ids: [],
    });
    setFormErrors({});
    setModalOpen(true);
    fetchProducts(defaultLocationId || undefined);
    // Focus name field after modal renders
    setTimeout(() => nameInputRef.current?.focus(), 100);
  }, [fetchProducts, defaultLocationId]);

  const openEditModal = useCallback((group: ModifierGroup) => {
    setEditingGroup(group);
    setForm({
      name: group.name,
      location_id: group.location_id,
      required: group.required,
      max_selections: group.max_selections ? String(group.max_selections) : '',
      options: group.options.map((o) => ({
        key: o.id,
        name: o.name,
        price_display: priceMinorToDisplay(o.price_adjustment, currency),
      })),
      product_ids: group.product_ids,
    });
    setFormErrors({});
    setModalOpen(true);
    fetchProducts(group.location_id);
    setTimeout(() => nameInputRef.current?.focus(), 100);
  }, [fetchProducts, currency]);

  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};
    if (!form.name.trim()) errors.name = 'Group name is required';
    if (form.name.trim().length > 100) errors.name = 'Max 100 characters';
    if (!form.location_id) errors.location_id = 'Select a location';

    // Validate each option name individually
    const optionNameErrors: Record<number, string> = {};
    let hasAtLeastOneOption = false;
    form.options.forEach((o, i) => {
      if (o.name.trim()) {
        hasAtLeastOneOption = true;
        if (o.name.trim().length > 100) {
          optionNameErrors[i] = 'Max 100 characters';
        }
      }
    });
    if (!hasAtLeastOneOption) errors.options = 'At least one option is required';
    if (Object.keys(optionNameErrors).length > 0) errors.option_names = optionNameErrors;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const validOptions = form.options
        .filter((o) => o.name.trim())
        .map((o) => ({
          name: o.name.trim(),
          price_adjustment: priceDisplayToMinor(o.price_display, currency),
        }));

      if (editingGroup) {
        await modifierService.update(editingGroup.id, {
          name: form.name.trim(),
          required: form.required,
          max_selections: form.max_selections ? Number(form.max_selections) : undefined,
          options: validOptions,
          product_ids: form.product_ids,
        });
        toast({ title: 'Modifier group updated', variant: 'success' });
      } else {
        await modifierService.create({
          name: form.name.trim(),
          location_id: form.location_id,
          required: form.required,
          max_selections: form.max_selections ? Number(form.max_selections) : undefined,
          options: validOptions,
          product_ids: form.product_ids.length > 0 ? form.product_ids : undefined,
        });
        toast({ title: `Modifier group '${form.name.trim()}' created`, variant: 'success' });
      }
      setModalOpen(false);
      fetchGroups();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save modifier group.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, form, editingGroup, fetchGroups, toast, currency]);

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await modifierService.deactivate(deleteTarget.id);
      toast({ title: 'Modifier group deleted', variant: 'success' });
      setDeleteTarget(null);
      fetchGroups();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete modifier group.';
      if (msg.includes('MODIFIER_IN_USE') || msg.toLowerCase().includes('in use') || msg.toLowerCase().includes('active orders')) {
        setDeleteError('Cannot delete — this modifier is used in active orders.');
      } else {
        toast({ title: msg, variant: 'error' });
        setDeleteTarget(null);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchGroups, toast]);

  // ── Option row handlers ─────────────────────────────────────────────────
  const addOptionRow = useCallback(() => {
    setForm((prev) => ({ ...prev, options: [...prev.options, EMPTY_OPTION()] }));
  }, []);

  const removeOptionRow = useCallback((key: string) => {
    setForm((prev) => ({ ...prev, options: prev.options.filter((o) => o.key !== key) }));
    setFormErrors((prev) => ({ ...prev, options: undefined, option_names: undefined }));
  }, []);

  const updateOptionRow = useCallback((key: string, field: 'name' | 'price_display', value: string) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o) => (o.key === key ? { ...o, [field]: value } : o)),
    }));
    setFormErrors((prev) => ({ ...prev, options: undefined, option_names: undefined }));
  }, []);

  // ── Handle Enter key in option fields to add new row ────────────────────
  // Input component doesn't forward onKeyDown so we listen on the wrapper div.
  const handleOptionKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
      e.preventDefault();
      addOptionRow();
    }
  }, [addOptionRow]);

  // ── Location change handler (refetch products) ──────────────────────────
  const handleLocationChange = useCallback((val: string | string[]) => {
    const locId = Array.isArray(val) ? val[0] : val;
    setForm((prev) => ({ ...prev, location_id: locId, product_ids: [] }));
    setFormErrors((prev) => ({ ...prev, location_id: undefined }));
    fetchProducts(locId);
  }, [fetchProducts]);

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns: ColumnDef<ModifierGroup>[] = useMemo(() => [
    {
      key: 'name', header: 'Group Name',
      render: (_v: unknown, row: ModifierGroup) => (
        <button type="button" onClick={() => openEditModal(row)}
          className="font-bold text-neutral-900 dark:text-neutral-100 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-left">
          {row.name}
        </button>
      ),
    },
    {
      key: 'options_count', header: 'Options', numeric: true,
      render: (_v: unknown, row: ModifierGroup) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.options.length} <span className="text-neutral-400 text-xs">option{row.options.length !== 1 ? 's' : ''}</span>
        </span>
      ),
    },
    {
      key: 'price_range', header: 'Price Adj.',
      render: (_v: unknown, row: ModifierGroup) => (
        <span className="font-mono tabular-nums text-sm text-neutral-600 dark:text-neutral-400">
          {paiseToPriceRange(row.options, currency)}
        </span>
      ),
    },
    {
      key: 'required', header: 'Required',
      render: (_v: unknown, row: ModifierGroup) => (
        <Badge variant={row.required ? 'warning' : 'draft'} size="sm">
          {row.required ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'max_selections', header: 'Max Sel.', numeric: true,
      render: (_v: unknown, row: ModifierGroup) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.max_selections ?? 'Unlimited'}
        </span>
      ),
    },
    {
      key: 'linked_products', header: 'Linked', numeric: true,
      render: (_v: unknown, row: ModifierGroup) => (
        <span className="font-mono tabular-nums text-neutral-600 dark:text-neutral-400">
          {row.product_ids.length} <span className="text-xs">product{row.product_ids.length !== 1 ? 's' : ''}</span>
        </span>
      ),
    },
    {
      key: 'actions', header: '',
      render: (_v: unknown, row: ModifierGroup) => (
        <div className="flex items-center gap-1 justify-end">
          <Button variant="ghost" size="sm" onClick={() => openEditModal(row)} aria-label={`Edit ${row.name}`}>
            <PencilIcon />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setDeleteError(null); setDeleteTarget(row); }}
            className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
            aria-label={`Delete ${row.name}`}>
            <TrashIcon />
          </Button>
        </div>
      ),
    },
  ], [openEditModal, currency]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <PageContainer
      title="Item Modifiers"
      subtitle={`${total} modifier group${total !== 1 ? 's' : ''}`}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Modifiers' }]}
      actions={
        <>
          {/* Desktop add button */}
          <div className="hidden sm:block">
            <Tooltip content="Press N to add (keyboard shortcut)">
              <Button variant="primary" size="sm" onClick={openCreateModal}>
                <PlusIcon /><span className="ml-1.5">Add Modifier Group</span>
              </Button>
            </Tooltip>
          </div>
        </>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ KPI Row ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KpiMini label="Total Groups" value={total} tone="violet" />
          <KpiMini label="Required Groups" value={requiredGroups} tone="brand" />
          <KpiMini label="Avg Options / Group" value={avgOptions} tone="teal" />
        </div>

        {/* ═══ Search ═══ */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="max-w-md">
            <Input
              ref={searchRef}
              variant="search"
              value={search}
              onChange={(val) => { setSearch(val); setPage(1); }}
              placeholder="Search modifier groups... (press / to focus)"
              aria-label="Search modifier groups"
            />
          </div>
        </div>

        {/* ═══ Desktop Data Table (hidden on xs–sm) ═══ */}
        <div className={cn(GLASS, 'overflow-hidden hidden md:block')}>
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="rect" width={50} height={24} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="rect" width={64} height={24} />
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="p-12">
              <EmptyState
                variant="inline"
                icon={<SlidersIcon className="h-14 w-14 text-violet-400" />}
                title="No modifier groups"
                description="Add customization options like spice level, add-ons, and cooking preferences"
                action={{ label: 'Add Modifier Group', onClick: openCreateModal }}
              />
            </div>
          ) : (
            <DataTable<ModifierGroup> columns={columns} data={groups} />
          )}
        </div>

        {/* ═══ Mobile Card List (visible only on xs–sm) ═══ */}
        <div className="block md:hidden space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={cn(GLASS, 'p-4')}>
                  <Skeleton variant="text" width="60%" height={16} />
                  <Skeleton variant="text" width="40%" height={14} />
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className={cn(GLASS, 'p-12')}>
              <EmptyState
                variant="inline"
                icon={<SlidersIcon className="h-14 w-14 text-violet-400" />}
                title="No modifier groups"
                description="Add customization options like spice level, add-ons, and cooking preferences"
                action={{ label: 'Add Modifier Group', onClick: openCreateModal }}
              />
            </div>
          ) : (
            groups.map((g) => (
              <ModifierMobileCard
                key={g.id}
                group={g}
                onEdit={openEditModal}
                onDelete={(grp) => { setDeleteError(null); setDeleteTarget(grp); }}
              />
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination currentPage={page} totalPages={totalPages} totalItems={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </div>
        )}

        {/* ═══ Mobile Floating Add Button ═══ */}
        <div className="fixed bottom-6 right-6 z-40 sm:hidden">
          <button
            type="button"
            onClick={openCreateModal}
            className="h-14 w-14 rounded-full bg-gradient-to-br from-violet-600 to-pink-500 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            aria-label="Add Modifier Group"
          >
            <PlusIcon />
          </button>
        </div>
      </div>

      {/* ═══ Create/Edit Modal ═══ */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingGroup ? `Edit "${editingGroup.name}"` : 'Add Modifier Group'} variant="wide">
        <div className="space-y-5 p-1 max-h-[70vh] overflow-y-auto">
          {/* Group Name */}
          <FormField label="Group Name" required error={formErrors.name}>
            <Input
              ref={nameInputRef}
              value={form.name}
              onChange={(val) => { setForm((p) => ({ ...p, name: val })); setFormErrors((p) => ({ ...p, name: undefined })); }}
              placeholder="e.g. Spice Level, Toppings, Cooking Preference"
              aria-label="Modifier group name"
            />
          </FormField>

          {/* Location (OWNER+ sees all; a MANAGER picks among their own, or has theirs auto-selected) */}
          {isOwnerPlus || locationOptions.length > 1 ? (
            <FormField label="Location" required error={formErrors.location_id}>
              <Select
                options={locationOptions}
                value={form.location_id}
                onChange={handleLocationChange}
                variant="searchable"
                placeholder="Select location..."
                required
              />
            </FormField>
          ) : (
            <input type="hidden" value={form.location_id} />
          )}

          {/* Required + Max Selections */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Mandatory Selection">
              <div className="flex items-center gap-3 mt-1">
                <Toggle checked={form.required} onChange={(val) => setForm((p) => ({ ...p, required: val }))} aria-label="Required modifier" />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">{form.required ? 'Customer must select' : 'Optional'}</span>
              </div>
            </FormField>
            <FormField label="Max Selections" helperText="Leave empty for unlimited">
              <Input
                value={form.max_selections}
                onChange={(val) => setForm((p) => ({ ...p, max_selections: val }))}
                type="number"
                placeholder="Unlimited"
                aria-label="Maximum selections"
              />
            </FormField>
          </div>

          {/* Options */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Options <span className="ml-0.5 text-danger-500" aria-hidden="true">*</span>
              </span>
              <Button variant="ghost" size="sm" onClick={addOptionRow} aria-label="Add another modifier option">
                <PlusIcon /><span className="ml-1">Add Option</span>
              </Button>
            </div>
            {formErrors.options && <p className="text-xs text-danger-600 dark:text-danger-400 mb-2" role="alert">{formErrors.options}</p>}
            {/* onKeyDown on wrapper: Input doesn't forward onKeyDown */}
            <div className="space-y-2" onKeyDown={handleOptionKeyDown}>
              {form.options.map((opt, idx) => (
                <div key={opt.key} className="flex items-center gap-2" aria-label={`Option ${idx + 1}`}>
                  <div className="flex-1">
                    <Input
                      value={opt.name}
                      onChange={(val) => updateOptionRow(opt.key, 'name', val)}
                      placeholder={`Option ${idx + 1} name`}
                      aria-label={`Option ${idx + 1} name`}
                    />
                    {formErrors.option_names?.[idx] && (
                      <p className="text-xs text-danger-600 mt-0.5" role="alert">{formErrors.option_names[idx]}</p>
                    )}
                  </div>
                  <div className="w-32">
                    <Input
                      value={opt.price_display}
                      onChange={(val) => updateOptionRow(opt.key, 'price_display', val)}
                      type="number"
                      placeholder={`${getCurrencySymbol(currency)} Price adj.`}
                      aria-label={`Option ${idx + 1} price adjustment in rupees`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOptionRow(opt.key)}
                    disabled={form.options.length <= 1}
                    className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20 shrink-0"
                    aria-label={`Remove option ${idx + 1}`}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Linked Products (multi-select with tags) */}
          <FormField
            label={`Applicable Products (${form.product_ids.length} selected)`}
            helperText="Leave empty to apply to all menu items"
          >
            <Select
              options={productOptions}
              value={form.product_ids}
              onChange={(val) => setForm((p) => ({ ...p, product_ids: Array.isArray(val) ? val : [val] }))}
              variant="multi-select"
              placeholder="Search and select products..."
              loading={products.length === 0 && modalOpen}
            />
          </FormField>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-neutral-100 dark:border-neutral-700/50">
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : editingGroup ? 'Save Changes' : 'Create Group'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ═══ Delete Confirm ═══ */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete "${deleteTarget?.name ?? ''}"?`}
        description={deleteError ?? 'This will deactivate the modifier group and its options. It will no longer be available for new orders.'}
        confirmLabel="Delete"
        variant="danger"
        loading={isDeleting}
        onConfirm={deleteError ? () => setDeleteTarget(null) : handleDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
      />
    </PageContainer>
  );
}

export function ModifierManagementPage() {
  return <ErrorBoundary><ModifierManagementContent /></ErrorBoundary>;
}
